import { type FC, useCallback, useMemo } from 'react';

import { formatDateToCanonical, getDateFromCanonical } from '@/helpers/questionnaires/signatureDate';
import type { SignatureFieldValue } from '@/typings/questionnaires-api-models';

import { DatePicker } from '../DatePicker';
import { InputOnBlur } from '../Input';
import Fieldset from '../shared/Fieldset';

type SignatureError = {
  text?: string;
  date?: string;
};

/** Props for the SignatureField compound input (text + date). */
export type SignatureFieldProps = {
  label: string;
  description?: string;
  disabled?: boolean;
  value: SignatureFieldValue | null;
  error?: string | SignatureError;
  optional?: boolean;
  hint?: string;
  onChange: (value: SignatureFieldValue) => void;
  onBlur?: () => void;
  onFocus?: () => void;
};

/** Compound form field for capturing a typed signature and date. */
const SignatureField: FC<SignatureFieldProps> = ({
  label,
  description,
  disabled,
  value,
  error,
  optional,
  hint,
  onChange,
  onBlur,
  onFocus,
}) => {
  const parsedDate = useMemo(() => getDateFromCanonical(value?.date ?? null), [value?.date]);

  const handleTextChange = useCallback(
    (nextText: string) => {
      onChange({ date: value?.date ?? null, text: nextText });
    },
    [onChange, value?.date],
  );

  const handleDateChange = useCallback(
    (nextDate: Date | null) => {
      if (!nextDate) return;
      onChange({ date: formatDateToCanonical(nextDate), text: value?.text ?? '' });
    },
    [onChange, value?.text],
  );

  const handleFieldBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  return (
    <Fieldset
      label={label}
      description={description}
      required={!optional}
      hint={hint}
      error={typeof error === 'string' ? error : undefined}
    >
      <div className='flex flex-col gap-2' role='none'>
        <InputOnBlur
          label='Signature'
          value={value?.text ?? ''}
          placeholder='/S/ '
          optional={optional}
          disabled={disabled}
          error={typeof error === 'object' ? error.text : undefined}
          onChange={handleTextChange}
          onBlur={handleFieldBlur}
          onFocus={onFocus}
        />
        <DatePicker
          label='Date'
          value={parsedDate}
          disabled={disabled}
          error={typeof error === 'object' ? error.date : undefined}
          onChange={handleDateChange}
          onBlur={handleFieldBlur}
          onFocus={onFocus}
        />
      </div>
    </Fieldset>
  );
};

export default SignatureField;
